import React from "react";
import CatalogItem from "../components/CatalogItem";
import Item from "../models/item";
import PageTitle from "../components/PageTitle";
import { Helmet } from "react-helmet";
import { Settings } from "../configuration/settings";
import { Link } from "react-router-dom";
import { ModelDetails } from "../configuration/model-description";
import Breadcrumbs from "../components/Breadcrumbs";
import Faq from "../components/Faq";
import ModelLinks from "../components/ModelLinks";
import ModelCards from "../components/ModelCards";
import SeoCollapse from "../components/SeoCollapse";

type CatalogProps = {
    items: Item[];
    outOfStocksVisible?: boolean;
    title?: string;
    metaDescription?: string;
    canonicalRelativeUrl?: string;
    breadcrumbs?: { text: string, url?: string, title?: string }[],
    header?: string;
    topHtml?: string;
    bottomHtml?: string;
    faq?: { title: string, items: { question: string, answer: string }[] };
};
type CatalogState = {
    groupedByIdItems: Item[][];
    modelsFilter: number[],
    waistFilter: number[],
    lengthFilter: number[],
};

enum SortType {
    byPriceAsc,
    byPriceDesc,
    byNewest,
    byRating
}

class Catalog extends React.Component<CatalogProps, CatalogState> {
    groupedByIdItems: Item[][] = [];

    constructor(props: CatalogProps) {
        super(props);

        props.items.forEach((item) => {
            if (!props.outOfStocksVisible && item.stockStatus == "outOfStock") {
                return; // do not show OutOfStock items in catalog
            }

            const group = this.groupedByIdItems.find((group) => group[0].id == item.id);
            if (group) {
                group.push(item);
            } else {
                this.groupedByIdItems.push([item]);
            }
        });

        this.state = {
            groupedByIdItems: this.groupedByIdItems,
            modelsFilter: [],
            waistFilter: [],
            lengthFilter: []
        };

        // This binding is necessary to make `this` work in the callback
        this.filterByModel = this.filterByModel.bind(this);
        this.filterByWaist = this.filterByWaist.bind(this);
        this.filterByLength = this.filterByLength.bind(this);
        this.resetFilter = this.resetFilter.bind(this);
        this.sort = this.sort.bind(this);
    }

    filterByModel(model: number) {
        let newModelsFilter: number[] = this.state.modelsFilter;
        if (newModelsFilter.indexOf(model) >= 0) {
            newModelsFilter = newModelsFilter.filter(x => x != model);
        }
        else {
            newModelsFilter.push(model);
        }

        this.setState({
            modelsFilter: newModelsFilter
        });
    }

    filterByWaist(waist: number) {
        let newWaistFilter: number[] = this.state.waistFilter;
        if (newWaistFilter.indexOf(waist) >= 0) {
            newWaistFilter = newWaistFilter.filter(x => x != waist);
        }
        else {
            newWaistFilter.push(waist);
        }

        this.setState({
            waistFilter: newWaistFilter
        });
    }

    filterByLength(length: number) {
        let newLengthFilter: number[] = this.state.lengthFilter;
        if (newLengthFilter.indexOf(length) >= 0) {
            newLengthFilter = newLengthFilter.filter(x => x != length);
        }
        else {
            newLengthFilter.push(length);
        }

        this.setState({
            lengthFilter: newLengthFilter
        });
    }

    resetFilter() {
        this.setState({
            modelsFilter: [],
            waistFilter: [],
            lengthFilter: []
        });
    }

    sort(event: any) {
        let sortedGroupedByIdInStockItems = this.groupedByIdItems.filter(x => x[0].stockStatus != "outOfStock");
        let sortedGroupedByIdOutOfStockItems = this.groupedByIdItems.filter(x => x[0].stockStatus == "outOfStock");

        if (event.target.value == SortType.byPriceAsc) {
            sortedGroupedByIdInStockItems.sort((a, b) => a[0].price - b[0].price);
            sortedGroupedByIdOutOfStockItems.sort((a, b) => a[0].price - b[0].price);
        }
        else if (event.target.value == SortType.byPriceDesc) {
            sortedGroupedByIdInStockItems.sort((a, b) => b[0].price - a[0].price);
            sortedGroupedByIdOutOfStockItems.sort((a, b) => b[0].price - a[0].price);
        }
        else if (event.target.value == SortType.byNewest) {
            // do nothing with sortedGroupedByIdOutOfStockItems as data is already properly sorted
            sortedGroupedByIdInStockItems = sortedGroupedByIdInStockItems.reverse();
        }
        else if (event.target.value == SortType.byRating) {
            // do nothing as data is already properly sorted
        }

        this.setState({
            groupedByIdItems: sortedGroupedByIdInStockItems.concat(sortedGroupedByIdOutOfStockItems)
        });
    }

    render() {
        const filteredGroupedByIdItems = this.state.groupedByIdItems.filter(group =>
            group.find(item =>
                (this.state.modelsFilter.length == 0 || this.state.modelsFilter.includes(item.model)) &&
                (this.state.waistFilter.length == 0 || this.state.waistFilter.includes(item.waist)) &&
                (this.state.lengthFilter.length == 0 || this.state.lengthFilter.includes(item.length))));

        return (
            <>
                <Helmet>
                    <title>{this.props.title || `Levi’s (Левіс, Левайс) - купити чоловічі джинси | ${Settings.shopName}`}</title>
                    <meta name="description" content={this.props.metaDescription || `Чоловічі джинси Levi’s (Левайс, Левис). Купуйте джинси Levis в інтернет-магазині ${Settings.shopName}. Товар в наявності, швидка доставка по Україні.`} />
                    <link rel="canonical" href={`${Settings.siteUrl}${this.props.canonicalRelativeUrl ?? ""}/`} />
                    <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "ItemList",
                        "itemListElement": [${filteredGroupedByIdItems.map((itemsGroup, index) => { 
                            const item = itemsGroup.find(x => x.stockStatus == "inStock") || itemsGroup[0];
                            return `
                            {"@type": "ListItem","position": ${index + 1},"item": {"@type": "Product","name": "Джинси ${item.name} ${item.color}","url": "${Settings.siteUrl}/p/${item.slug}/","image": "${item.imageFront}","sku": "${item.id}","offers": {"@type": "Offer","price": "${item.price}","priceCurrency": "UAH","availability": "https://schema.org/${item.stockStatus == "outOfStock" ? "OutOfStock" : item.stockStatus == "inStock" ? "InStock" : "PreOrder"}","seller":{"@type":"Organization","name":"${Settings.shopName}","url":"${Settings.siteUrl}"}}}}`;})}
                        ]
                    }`}
                    </script>
                </Helmet>
                { this.props.breadcrumbs && <Breadcrumbs items={this.props.breadcrumbs} /> }
                <PageTitle title={this.props.header || "Чоловічі джинси Levi’s"} />
                <section className="mb-1 text-secondary">
                { 
                    this.props.topHtml ? <div dangerouslySetInnerHTML={{__html: this.props.topHtml}}></div> :
                    <>
                        <p className="mb-2">
                            В інтернет-магазині <strong>{Settings.shopName}</strong> представлені популярні оригінальні чоловічі джинси <strong>Levi’s</strong>. 
                            У нас ви знайдете класичні та завужені моделі бренду з актуальними розмірами та реальними фото товарів. 
                            Купуйте джинси з перевіреною якістю та <strong>швидкою доставкою по всій Україні</strong>. 
                            Вибирайте свій ідеальний крій та стиль, щоб виглядати сучасно і комфортно кожного дня.
                        </p>
                        <ModelCards />
                    </>
                }
                </section>
                <aside className="row mb-3">
                    <div className="col-6 col-md-4 col-lg-3 pe-1">
                        <button type="button" className="btn btn-outline-secondary w-100" data-bs-toggle="offcanvas" data-bs-target="#offcanvasExample" aria-controls="offcanvasExample">
                            Фільтри {this.state.modelsFilter.length + this.state.waistFilter.length + this.state.lengthFilter.length > 0 ? <span className="fw-bold">({this.state.modelsFilter.length + this.state.waistFilter.length + this.state.lengthFilter.length})</span> : ""}
                        </button>
                        <div className="offcanvas offcanvas-start" tabIndex={-1} id="offcanvasExample" aria-labelledby="offcanvasExampleLabel">
                            <div className="offcanvas-header">
                                <h5 className="offcanvas-title" id="offcanvasExampleLabel">Фільтри</h5>
                            </div>
                            <div className="offcanvas-body">
                                <div className="text-start mb-3 w-100">Модель:</div>
                                <div className="float-start w-100 mb-3" role="group">
                                    {Array.from(new Set(this.props.items.map(x => x.model))).sort().map((model, index) => (
                                        <React.Fragment>
                                            <input type="checkbox" className="btn-check" id={"modelCheck" + index} autoComplete="off" checked={this.state.modelsFilter.indexOf(model) >= 0} onClick={() => this.filterByModel(model)} />
                                            <label className="btn btn-outline-secondary float-start me-2 mb-2" htmlFor={"modelCheck" + index}>{model}</label>
                                        </React.Fragment>
                                    ))}
                                </div>
                                <div className="text-start mb-3 w-100">Розмір талії (W):</div>
                                <div className="float-start w-100 mb-3" role="group">
                                    {Array.from(new Set(this.props.items.map(x => x.waist))).sort().map((waist, index) => (
                                        <React.Fragment>
                                            <input type="checkbox" className="btn-check" id={"waistCheck" + index} autoComplete="off" checked={this.state.waistFilter.indexOf(waist) >= 0} onClick={() => this.filterByWaist(waist)} />
                                            <label className="btn btn-outline-secondary float-start me-2 mb-2" htmlFor={"waistCheck" + index}>{waist}</label>
                                        </React.Fragment>
                                    ))}
                                </div>
                                <div className="text-start mb-3 w-100">Довжина (L):</div>
                                <div className="float-start w-100 mb-4">
                                    {Array.from(new Set(this.props.items.map(x => x.length))).sort().map((length, index) => (
                                        <React.Fragment>
                                            <input type="checkbox" className="btn-check" id={"lengthCheck" + index} autoComplete="off" checked={this.state.lengthFilter.indexOf(length) >= 0} onClick={() => this.filterByLength(length)} />
                                            <label className="btn btn-outline-secondary float-start me-2 mb-2" htmlFor={"lengthCheck" + index}>{length}</label>
                                        </React.Fragment>
                                    ))}
                                </div>
                                <div className="row border-top pt-3">
                                    <div className="col pe-2">
                                        <button className="btn btn-outline-secondary w-100" role="button" onClick={this.resetFilter}>Очистити</button>
                                    </div>
                                    <div className="col ps-0">
                                        <button type="button" className="btn btn-secondary w-100" data-bs-dismiss="offcanvas" aria-label="Показати товари">
                                            Показати ({filteredGroupedByIdItems.length})
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="d-none d-md-block col-md-4 col-lg-6"></div>
                    <div className="col-6 col-md-4 col-lg-3 ps-1">
                        <select className="form-select text-secondary border-secondary w-100" aria-label=".form-select-lg example" onChange={this.sort} defaultValue={SortType.byRating}>
                            <option value={SortType.byPriceAsc}>Спочатку дешевші</option>
                            <option value={SortType.byPriceDesc}>Спочатку дорожчі</option>
                            <option value={SortType.byNewest}>Новинки</option>
                            <option value={SortType.byRating}>За рейтингом</option>
                        </select>
                    </div>
                </aside>
                <div className="container border-bottom">
                    {filteredGroupedByIdItems.length > 0
                        ? <ul className="row ps-0 mb-0">
                            {filteredGroupedByIdItems.map((itemsGroup, index) => (
                                <li className="d-block col-6 col-md-4 col-lg-3 border-top border-end" key={index}>
                                    <CatalogItem item={itemsGroup.find(x => x.stockStatus == "inStock") || itemsGroup[0]} items={itemsGroup} showSizes={true} lazyLoad={true} />
                                </li>
                            ))}
                        </ul>
                        : <div className="row border-top">
                            <p className="mt-3">
                                Товари за обраними фільтрами не знайдені.
                                Будь ласка, змініть фільтри та спробуйте ще раз.
                                Щоб переглянути всі товари, натисніть кнопку нижче.
                            </p>
                            <div className="text-center mt-3 mb-5">
                                <button className="btn btn-secondary" role="button" onClick={this.resetFilter}>Переглянути всі</button>
                            </div>
                        </div>
                    }
                </div>
                <section className="row pt-3 text-secondary"><SeoCollapse>
                {
                    this.props.bottomHtml ? <div dangerouslySetInnerHTML={{__html: this.props.bottomHtml}}></div> : <>
                    <p>
                        <strong>Чоловічі джинси Levi’s</strong> - одні з найвідоміших та найпопулярніших у світі.
                        Бренд <strong>Levi Strauss &amp; Co.</strong> понад 100 років асоціюється з якістю,
                        надійністю та стилем, який не втрачає актуальності.
                    </p>
                    <p>
                        {Settings.shopName} з 2020 року спеціалізується на джинсах Levi’s для чоловіків 
                        і добре знає особливості кожної моделі, посадки та розмірної сітки бренду.
                        На цій сторінці представлений каталог усіх моделей,
                        доступних для замовлення онлайн з доставкою по Україні.
                    </p>

                    <h2 className="fs-5">Моделі чоловічих джинсів Левайс</h2>
                    <p>
                        У каталозі {Settings.shopName} ви знайдете популярні фасони, які добре сидять на чоловіках
                        різної статури та підходять для повсякденного носіння:
                    </p>
                    <div>
                        <ModelLinks maxCount={4} />
                    </div>
                    <p>
                        Кожна модель має унікальні особливості посадки та стилю, 
                        що дозволяє підібрати ідеальні джинси для будь-якого образу та ситуації.
                    </p>

                    <h2 className="fs-5">Стиль, кольори та комфорт</h2>
                    <p>
                        Джинси <strong>Левіс</strong> відомі стабільно високою якістю крою та пошиття.
                        Вони зручні, практичні та розраховані на тривале носіння.
                    </p>
                    <p>
                        У каталозі представлені моделі в різних кольорах та відтінках: популярні <strong>сині</strong> та стильні <strong>сірі</strong> джинси.
                    </p>
                    <p>
                        Чоловічі джинси <strong>Левайс</strong> доступні як на ґудзиках,
                        так і на блискавці. У продажу є моделі з класичного деніму,
                        а також зі стрейчевої тканини для більшого комфорту.
                    </p>

                    <h2 className="fs-5">Оригінальні джинси Levis в Україні</h2>
                    <p>
                        Усі джинси Levis у каталозі {Settings.shopName} є оригінальними та відповідають стандартам якості бренду.
                        Ми працюємо з перевіреними постачальниками та напряму завозимо продукцію без зайвих націнок.
                    </p>
                    <p>
                        У каталозі представлені джинси для будь-якої пори року -
                        весни, літа, осені або зими. Джинси Левис довго носяться,
                        добре тримають форму та залишаються актуальними багато сезонів.
                    </p>
                    <p>
                        Якщо ви хочете дізнатися, на що звертати увагу при покупці
                        , <Link className="link-secondary" to="/yak-pereviryty-oryhinalnist-dzhynsiv-levis/">
                            як перевірити оригінальність джинсів Levi’s
                        </Link>,
                        ми підготували детальний гід для покупців.
                    </p>

                    <h2 className="fs-5">Допомога з вибором розміру</h2>
                    <p>
                        Якщо ви не впевнені у виборі моделі або розміру,
                        наші консультанти завжди готові допомогти.
                        Ми підкажемо, яка модель Levi’s підійде саме вам,
                        та допоможемо правильно підібрати розмір.
                    </p>

                    <h2 className="fs-5">Чому варто купити чоловічі джинси Левис у {Settings.shopName}</h2>
                    <div>
                        <ul>
                            <li>лише оригінальні чоловічі джинси Levi’s</li>
                            <li>єдиний повний каталог на одній сторінці</li>
                            <li>інтернет-магазин працює з 2020 року</li>
                            <li>професійна допомога з вибором</li>
                            <li>доставка по всій Україні</li>
                        </ul>
                    </div>
                    </>
                }</SeoCollapse>
                </section>
                <section className="row text-secondary mb-3">
                    <h2 className="fs-5">{ this.props.faq?.title || "Часті питання про чоловічі джинси Levi’s" }</h2>
                    <Faq items={this.props.faq?.items || [
                        {
                            question: "Які чоловічі джинси Levi’s найпопулярніші?", 
                            answer: "Найпопулярніші моделі чоловічих джинсів Levi’s - 502 Taper, 511 Slim, 505 Regular та 501 Original." 
                        },
                        {
                            question: "Чим відрізняються різні моделі Levi’s?", 
                            answer: "Моделі відрізняються посадкою, шириною штанини та стилем: наприклад, Levi’s 501 і Levi’s 505 - класичний прямий крій на ґудзиках і блискавці відповідно, Levi’s 511 - злегка завужені slim по всій довжині, а Levi’s 502 - сучасний taper, який поєднує класичну посадку зверху з елегантним звуженням внизу штанини. Це допомагає підібрати джинси під ваш стиль і фігуру." 
                        },
                        {
                            question: "Чи всі джинси Levi’s в каталозі - оригінальні?", 
                            answer: `Так - всі джинси в каталозі ${Settings.shopName} є оригінальними і підтверджують стандарти бренду Levi Strauss & Co. Ми співпрацюємо з перевіреними постачальниками.` 
                        },
                        {
                            question: "Яка різниця між Levi’s Premium і звичайними джинсами Levi’s?", 
                            answer: "Різниця між Levi’s Premium і базовою лінійкою - у якості матеріалів та деталях обробки. Серія Premium шиється з більш щільного деніму, має покращену фурнітуру, акуратні внутрішні шви та фірмовий шкіряний патч, завдяки чому джинси довше зберігають форму й виглядають дорожче. Звичайні моделі Levi’s - це оригінальні джинси з тією ж фірмовою посадкою, але з простішими матеріалами та доступнішою ціною." 
                        },
                        {
                            question: "Чи джинси Levi’s підходять для щоденного носіння?", 
                            answer: "Так, чоловічі джинси Levi’s виготовляються з міцного деніму, добре тримають форму та підходять для щоденного використання - як у повсякденному, так і в casual-образі." 
                        }
                    ]} />
                </section>
            </>
        )
    }
}

export default Catalog;